import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileFrame from '../components/MobileFrame';
import LoadingAnimation from '../components/LoadingAnimation';
import axios from 'axios';
import { callPreApprovalService } from '../services/preApprovalService';
import { PreApprovalResponse, PreApprovalRequest } from '../types/preApproval';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://staging.api.pay.cleverpe.com/api';


const Otp: React.FC = () => {
  const [otp, setOtp] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpComplete, setOtpComplete] = useState(false); // 🚀 prevent auto re-focus
  const [mobileNumber, setMobileNumber] = useState('');
  const [partnerMobileNumber, setPartnerMobileNumber] = useState('');
  const [dob, setDob] = useState(''); // Add state for DOB
  const [remainingTime, setRemainingTime] = useState(0);
  const [timerInitialized, setTimerInitialized] = useState(false);
  const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const expiryTimeRef = useRef<number | null>(null);

  // Check for stored mobile number and transaction ID
  useEffect(() => {
    const storedMobile = sessionStorage.getItem('mobileNumber');
    const storedPartnerMobile = sessionStorage.getItem('partnerMobileNumber');
    const storedDob = sessionStorage.getItem('dob'); // Retrieve DOB from session storage
    const storedExpiresAt = sessionStorage.getItem('otpExpiresAt');

    if (storedMobile) {
      setMobileNumber(storedMobile);
    } else {
      // Redirect back to login if no mobile number
      navigate('/login');
    }

    if (storedPartnerMobile) {
      setPartnerMobileNumber(storedPartnerMobile);
    }

    if (storedDob) {
      setDob(storedDob);
    }

    // Store expiry time in ref and handle page refresh
    if (storedExpiresAt) {
      const expiryTimestamp = parseInt(storedExpiresAt);
      const now = Date.now();

      // Check if the stored expiry time is still in the future
      if (expiryTimestamp > now) {
        expiryTimeRef.current = expiryTimestamp;
        const remaining = Math.max(0, Math.floor((expiryTimestamp - now) / 1000));
        setRemainingTime(remaining);
      } else {
        // If expired, set remaining time to 0 and clear the stored expiry
        expiryTimeRef.current = null;
        setRemainingTime(0);
        sessionStorage.removeItem('otpExpiresAt');
      }
    } else {
      // Only set new expiry if there isn't one stored
      const threeMinutesFromNow = Date.now() + 3 * 60 * 1000;
      expiryTimeRef.current = threeMinutesFromNow;
      sessionStorage.setItem('otpExpiresAt', threeMinutesFromNow.toString());
      setRemainingTime(180);
    }

    setTimerInitialized(true);
  }, [navigate]);

  // Timer effect
  useEffect(() => {
    if (!timerInitialized) return;

    timerRef.current = setInterval(() => {
      if (expiryTimeRef.current) {
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((expiryTimeRef.current - now) / 1000));
        setRemainingTime(remaining);

        // Clear interval when expired
        if (remaining <= 0 && timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          expiryTimeRef.current = null; // reset expiry ref
        }
      }
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timerInitialized]);

  // Handle window resize to determine if mobile view should be shown
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Detect keyboard open on mobile
  useEffect(() => {
    if (!isMobile) return;

    const visualViewport = window.visualViewport;
    if (!visualViewport) return;

    const handleVisualViewportResize = () => {
      const isKeyboard = visualViewport.height < window.innerHeight * 0.75;
      setIsKeyboardOpen(isKeyboard);
    };

    visualViewport.addEventListener('resize', handleVisualViewportResize);
    return () => visualViewport.removeEventListener('resize', handleVisualViewportResize);
  }, [isMobile]);

  // Focus input on component mount
  useEffect(() => {
    if (otpComplete) return; // never focus if OTP complete

    if (isMobile) {
      inputRef.current?.focus({ preventScroll: true });
    } else {
      inputRef.current?.focus(); // normal desktop focus
    }
  }, [isMobile, otpComplete]);




  // Format the remaining time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Listen for otp changes to clear error state
  useEffect(() => {
    if (error && otp.length < 6) {
      setError('');
    }
  }, [otp, error]);


  // Add a debounced submit function to avoid multiple submissions
  const submitOtp = async () => {
    if (isLoading) return;

    if (otp.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }

    // 👇 Set loading and animation immediately!
    setIsLoading(true);
    setShowLoadingAnimation(true);
    setLoadingMessage('Verifying OTP...');

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/verify-otp`, {
        mobileNumber,
        otp,
      });

      // console.log('API Response:', response.data);

      if (!response.data.success) {
        setError(response.data.message || 'Invalid OTP. Please try again.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setIsLoading(false);
        setShowLoadingAnimation(false);
        setLoadingMessage('');
        return;
      }

      sessionStorage.setItem('authToken', response.data.data.token);

      setLoadingMessage('OTP Verified! ✓');
      await new Promise(resolve => setTimeout(resolve, 1000));

      setLoadingMessage('Fetching Pre Approval...');
      await new Promise(resolve => setTimeout(resolve, 500));

      // Don't set loading false here, let processPreApproval handle it
      await processPreApproval('', response.data.data.token);
    } catch (err: any) {
      // console.error('OTP verification error:', err);

      const apiMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'OTP verification failed. Please try again.';

      setError(apiMessage);
      setIsLoading(false);
      setShowLoadingAnimation(false);
      setLoadingMessage('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };






  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);

    if (value.length === 6) {
      setOtpComplete(true);

      // ✅ Only blur on mobile to close keyboard
      if (isMobile) {
        setTimeout(() => inputRef.current?.blur(), 100);
      }
    } else {
      setOtpComplete(false);
    }
  };





  // Click on OTP boxes
  const handleDigitBoxClick = () => {
    // Only focus if OTP is incomplete
    if (!otpComplete && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  };




  const handleContinue = () => {
    submitOtp(); // let all loading, error, validation happen there
  };

  // Process pre-approval after successful OTP verification
  const processPreApproval = async (transactionId: string, token: string) => {
    try {
      setIsLoading(true);
      setShowLoadingAnimation(true);
      // console.log('Processing pre-approval for transaction:', transactionId);
      // console.log('Using auth token:', token ? 'Valid token present' : 'No token');

      const partnerMobile = sessionStorage.getItem('partnerMobileNumber') || '';
      // console.log('Using partner mobile number for pre-approval:', partnerMobile);

      const transactionDataStr = sessionStorage.getItem('transactionData');
      // console.log('Transaction data from sessionStorage:', transactionDataStr ? 'Data present' : 'No data');
      // console.log('Raw transactionData:', transactionDataStr);

      // Get all user data from sessionStorage (use these as authoritative sources)
      const storedMobileNumber = sessionStorage.getItem('mobileNumber') || '';
      const storedName = sessionStorage.getItem('name') || '';
      const storedEmail = sessionStorage.getItem('email') || '';
      const storedPanNumber = sessionStorage.getItem('panNumber') || '';
      const storedIncome = parseFloat(sessionStorage.getItem('income') || '0');
      const storedEmploymentType = sessionStorage.getItem('employmentType') || '1';
      const storedDob = sessionStorage.getItem('dob') || '';
      const storedpincode = sessionStorage.getItem('pincode') || '';
      const storedorderId = sessionStorage.getItem('orderId') || '';

      // Prepare user data for pre-approval check
      let userData: PreApprovalRequest = {
        mobileNumber: storedMobileNumber,
        name: storedName,
        email: storedEmail,
        panNumber: storedPanNumber,
        income: storedIncome,
        employmentType: storedEmploymentType,
        dob: storedDob,
        pincode : storedpincode, 
        orderId: storedorderId, 
        partnerMobileNumber: partnerMobile, // Include partner mobile number in request body
      };

      // If we have transaction data, add orderId and partnerId
      if (transactionDataStr) {
        try {
          const transactionData = JSON.parse(transactionDataStr);
          if (transactionData.orderId) {
            userData.orderId = transactionData.orderId;
          }
          if (transactionData.partnerId) {
            userData.partnerId = transactionData.partnerId;
          }
        } catch (e) {
          // console.error('Error parsing transaction data:', e);
        }
      }

      // console.log('Final user data for pre-approval:', userData);

      // Update loading message
      setLoadingMessage('Processing your application...');

      // Call the pre-approval service with user data directly using the auth token
      const preApprovalResponse = await callPreApprovalService(token, userData);

      // console.log('Pre-approval service response:', preApprovalResponse);

      // Update loading message
      setLoadingMessage('Application processed! Redirecting...');

      // Process pre-approval response

      // Handle both response formats - the expected format with success property
      // and the direct format with Amount, Message, Lender properties
      if (preApprovalResponse.success) {
        // Standard response format with success property
        sessionStorage.setItem('preApprovalResult', JSON.stringify(preApprovalResponse.data));

        // Add a slight delay to ensure loading animation is visible
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Navigate based on approval result
        if (preApprovalResponse.data && preApprovalResponse.data.amount > 0) {
          // Successfully approved with an amount
          navigate('/success', {
            state: {
              preApprovalResult: preApprovalResponse.data
            }
          });
        } else {
          // Approved but with zero amount or rejection
          navigate('/fail', {
            state: {
              message: 'Pre-approval was not successful.',
              details: preApprovalResponse.message
            }
          });
        }
      } else if (preApprovalResponse.Amount !== undefined) {
        // Direct response format (Amount, Message, Lender)
        // Convert to expected format for consistency
        const formattedResponse = {
          amount: preApprovalResponse.Amount,
          message: preApprovalResponse.Message,
          lender: preApprovalResponse.Lender
        };

        sessionStorage.setItem('preApprovalResult', JSON.stringify(formattedResponse));

        // Add a slight delay to ensure loading animation is visible
        await new Promise(resolve => setTimeout(resolve, 1000));

        if (formattedResponse.amount > 0) {
          // Successfully approved with an amount
          navigate('/success', {
            state: {
              preApprovalResult: formattedResponse
            }
          });
        } else {
          // Rejected with zero amount
          navigate('/fail', {
            state: {
              message: 'Pre-approval was not successful.',
              details: formattedResponse.message
            }
          });
        }
      } else {
        // Neither format matched
        await new Promise(resolve => setTimeout(resolve, 1000));
        throw new Error(preApprovalResponse.message || 'Pre-approval process failed');
      }
    } catch (err: any) {
      // console.error('Pre-approval error:', err);
      // Don't hide the loading animation immediately
      setIsLoading(true); // Keep loading state active
      setError(err.response?.data?.message || err.message || 'Pre-approval process failed. Please try again.');

      // Keep showing loading animation and navigate to failure page after a short delay
      setShowLoadingAnimation(true); // Ensure loading animation stays visible
      setLoadingMessage('Something went wrong. Redirecting...');

      setTimeout(() => {
        navigate('/fail', {
          state: {
            message: 'Pre-approval process failed.',
            details: err.message
          }
        });
      }, 2000);
    }
  };

  const handleResendOtp = async () => {
    // Don't allow resend if timer is still active
    if (remainingTime > 0) {
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      // Reset the OTP input
      setOtp('');

      // console.log('Requesting new OTP for mobile:', mobileNumber);

      // Call the login API to resend OTP
      const payload = {
        mobileNumber,
      };

      const response = await axios.post(`${API_BASE_URL}/auth/login`, payload);

      // console.log('Resend OTP response:', response.data);

      if (response.data.success) {
        // Focus input
        if (!otpComplete && inputRef.current) {
          inputRef.current.focus();
        }

        // Store the new expiration timestamp if available
        if (response.data.expiresAt) {
          const expiresAt = response.data.expiresAt;
          sessionStorage.setItem('otpExpiresAt', expiresAt.toString());
          expiryTimeRef.current = expiresAt;
          const now = Date.now();
          const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
          setRemainingTime(remaining);
        } else {
          // Fallback to 3 minutes if no timestamp is available
          const threeMinutesFromNow = Date.now() + 3 * 60 * 1000;
          expiryTimeRef.current = threeMinutesFromNow;
          sessionStorage.setItem('otpExpiresAt', threeMinutesFromNow.toString());
          setRemainingTime(180);
        }

        // Restart the timer if it was stopped
        if (!timerRef.current) {
          timerRef.current = setInterval(() => {
            if (expiryTimeRef.current) {
              const now = Date.now();
              const remaining = Math.max(0, Math.floor((expiryTimeRef.current - now) / 1000));

              setRemainingTime(remaining);

              if (remaining <= 0 && timerRef.current) {
                clearInterval(timerRef.current);
              }
            }
          }, 1000);
        }
      } else {
        setError(response.data.message || 'Failed to resend OTP. Please try again.');
      }
    } catch (err: any) {
      // console.error('Resend OTP error:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to resend OTP. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Render individual digit boxes for visual display
  const renderDigitBoxes = () => {
    const boxes = [];
    for (let i = 0; i < 6; i++) {
      boxes.push(
        <div
          key={i}
          className={`w-12 h-12 border rounded-xl flex items-center justify-center bg-white dark:bg-dark-card
            ${isLoading ? 'opacity-70' : ''}
            ${otp[i] ? 'border-gray-300 dark:border-gray-600' : 'border-gray-200 dark:border-gray-700'}`}
          onClick={() => inputRef.current?.focus()}
        >
          <span className="text-xl font-medium dark:text-white">{otp[i] || ''}</span>
          {!otp[i] && i === otp.length && !isLoading && (
            <div className="h-5 w-0.5 bg-gray-500 dark:bg-gray-400 animate-pulse ml-0.5"></div>
          )}
          {isLoading && i === 5 && otp.length === 6 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-5 h-5 border-t-2 border-blue-500 rounded-full dark:border-app-blue animate-spin"></div>
            </div>
          )}
        </div>
      );
    }
    return boxes;
  };

  // Add a function to render the timer or enable resend option
  // Correct renderTimer logic
  const renderTimer = () => {
    if (remainingTime > 0) {
      return (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          Expires in {formatTime(remainingTime)}
        </span>
      );
    }

    // Expired
    return (
      <span className="text-sm font-medium text-red-600 dark:text-red-500">
        OTP Expired
      </span>
    );
  };

  // Update the UI to render the resend button with appropriate disabled state
  const renderResendButton = () => {
    // Button should be disabled if timer is still active or if loading
    const isDisabled = remainingTime > 0 || isLoading;

    return (
      <div className="mb-6 text-center">
        <span className="text-gray-600 dark:text-gray-400">Didn't receive code? </span>
        <button
          onClick={handleResendOtp}
          disabled={isDisabled}
          className={`font-medium ${isDisabled ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' : 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300'}`}
        >
          resend it
        </button>
      </div>
    );
  };

  const OtpContent: React.FC = () => {
    return (
      <div className={`flex flex-col h-full ${isKeyboardOpen ? 'justify-start' : ''}`}>
        {/* Page indicator only */}
        <div className="flex items-center justify-end mb-4">
          <div className="text-base font-semibold text-gray-700 dark:text-gray-300">2<span className="font-normal text-gray-400 dark:text-gray-500">/3</span></div>
        </div>

        {/* Main content */}
        <div className={`${isKeyboardOpen ? 'mb-4' : 'flex-1'}`}>
          <h1 className="text-[1.6rem] font-extrabold mb-4 text-gray-900 dark:text-white">Enter OTP</h1>
          <p className="mb-4 text-gray-600 dark:text-gray-400">We've sent you a code on your registered mobile number</p>


          <div className="mb-6">
            <div className="flex justify-between mb-4">
              <label htmlFor="otp-input" className="block text-[0.98rem] text-gray-500 dark:text-gray-400 font-medium">OTP</label>
              {renderTimer()}
            </div>

            {/* Hidden input for actual keyboard interaction */}
            <input
              id="otp-input"
              ref={inputRef}
              type="tel"
              inputMode="numeric"
              pattern="\d*"
              maxLength={6}
              value={otp}
              onKeyDown={(e) => {
                // Enter / Go
                if ((e.key === 'Enter' || e.key === 'Go') && otp.length === 6 && !isLoading) {
                  handleContinue();
                }

                // Backspace logic
                if (e.key === 'Backspace') {
                  setOtp((prev) => prev.slice(0, -1));
                  setOtpComplete(false);
                  e.preventDefault(); // prevent browser default navigation behavior
                }
              }}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setOtp(value);

                if (value.length === 6) {
                  setOtpComplete(true);

                  // close keyboard on mobile
                  // setTimeout(() => inputRef.current?.blur(), 100);
                } else {
                  setOtpComplete(false);
                }
              }}
              className="absolute top-2 left-2 w-12 h-12 opacity-0"
              autoComplete="one-time-code"
              disabled={isLoading}
              autoFocus
            />




            {/* Visual OTP boxes */}
            <div className="flex gap-2 mb-2" onClick={handleDigitBoxClick}>
              {renderDigitBoxes()}
            </div>




            {/* 🔽 Error goes here 🔽 */}
            <div className="min-h-[48px] transition-all duration-300 ease-in-out">
              {error && (
                <div className="p-3 text-sm text-red-700 border border-red-200 rounded-lg dark:text-red-400 dark:border-red-900 bg-red-50 dark:bg-red-900/20">
                  {error}
                </div>
              )}
            </div>
          </div>

          {renderResendButton()}
        </div>

        {/* Footer section - conditionally displayed based on keyboard state */}
        <div className={`mt-auto ${isKeyboardOpen && isMobile ? 'pb-2' : ''}`}>
          {/* Powered by CleverPe - hide when keyboard is open on mobile */}
          {(!isKeyboardOpen || !isMobile) && (
            <div className="flex items-center justify-center mb-4">
              <img
                src="/CleverPeLogos/PoweredBy.svg"
                alt="Powered By CleverPe"
                className="h-10"
                onError={(e) => {
                  e.currentTarget.src = 'https://placehold.co/140x40?text=Powered+By+CleverPe';
                }}
              />
            </div>
          )}

          {/* Continue button */}
          <button
            onClick={handleContinue}
            disabled={isLoading || otp.length !== 6}
            className={`w-full py-3 rounded-full text-center font-semibold text-lg transition
              ${isLoading || otp.length !== 6
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md active:scale-[0.97]'}`}
          >
            {isLoading ? 'Verifying...' : 'Verify & Continue'}
          </button>
        </div>
      </div>
    );
  };

  // If showing loading animation, use full height container in both views
  if (showLoadingAnimation) {
    return isMobile ? (
      <div className="flex items-center justify-center h-screen bg-white dark:bg-dark">
        <LoadingAnimation message={loadingMessage} />
      </div>
    ) : (
      <MobileFrame>
        <div className="flex items-center justify-center h-full">
          <LoadingAnimation message={loadingMessage} />
        </div>
      </MobileFrame>
    );
  }

  // Regular OTP content
  return isMobile ? (
    <div className="h-[100dvh] w-full p-5 overflow-hidden bg-white dark:bg-dark">
      <OtpContent />
    </div>
  ) : (
    <MobileFrame>
      <OtpContent />
    </MobileFrame>
  );
};

export default Otp;